import { ArrowUpRight, CalendarDays, Users } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import Newsletter from "@/components/Newsletter";
import { DESTINATIONS, LINKS } from "@/data/content";

export default function Destinations() {
  return (
    <div data-testid="destinations-page">
      <section className="relative grain-overlay overflow-hidden">
        <div className="absolute top-0 left-1/3 h-[380px] w-[380px] rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 sm:pt-44 pb-16 sm:pb-20">
          <Reveal>
            <Eyebrow>Destination Guides</Eyebrow>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight max-w-3xl leading-[1.08]">
              Places worth the <span className="italic text-ocean">planning.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-charcoal/75 max-w-2xl leading-relaxed">
              City, beach, and weekend-trip guides that balance inspiration with the details that matter — where
              to stay, what to do, when to go, how to get around, and what to book ahead.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 sm:pb-28">
        <div className="grid md:grid-cols-2 gap-6">
          {DESTINATIONS.map((d, i) => (
            <Reveal key={d.slug} delay={(i % 2) * 0.1}>
              <article
                data-testid={`guide-card-${d.slug}`}
                className="group h-full rounded-3xl bg-white border border-slateblue/15 overflow-hidden transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_56px_-20px_rgba(16,42,67,0.2)] hover:border-gold/40"
              >
                <div className="relative h-64 sm:h-72 overflow-hidden">
                  <img
                    src={d.image}
                    alt={d.title}
                    className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-5 flex items-center gap-3">
                    <span className="rounded-full bg-ivory/90 backdrop-blur px-3.5 py-1.5 text-[10px] font-semibold tracking-[0.16em] uppercase text-navy">
                      {d.city}, {d.region}
                    </span>
                  </div>
                </div>
                <div className="p-7">
                  <h2 className="font-serif text-2xl text-navy leading-snug">{d.title}</h2>
                  <p className="mt-3 text-sm text-charcoal/70 leading-relaxed">{d.blurb}</p>
                  <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slateblue">
                    <span className="inline-flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5 text-gold" />
                      {d.bestFor}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5 text-gold" />
                      {d.days}
                    </span>
                  </div>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <a
                      href={d.booking}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      data-testid={`guide-booking-${d.slug}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-navy px-5 py-2.5 text-xs font-semibold text-ivory transition-colors duration-300 hover:bg-ocean"
                    >
                      Compare hotels in {d.city}
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </a>
                    <a
                      href={LINKS.viator}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      data-testid={`guide-tours-${d.slug}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-5 py-2.5 text-xs font-semibold text-navy transition-colors duration-300 hover:border-gold hover:text-gold"
                    >
                      See tour options and dates
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 text-xs text-slateblue leading-relaxed max-w-2xl" data-testid="destinations-disclosure-note">
            Booking and tour buttons link to our travel partners. If you book through them, we may earn a
            commission at no additional cost to you — it never changes our recommendations.
          </p>
        </Reveal>
      </section>
      <Newsletter />
    </div>
  );
}
